import MuiChip, { ChipProps as MuiChipProps } from '@mui/material/Chip';
import CircleIcon from '@mui/icons-material/Circle';
import React from 'react';

export type ChipColorVariant =
    | 'success'
    | 'info'
    | 'muted'
    | 'error'
    | 'warning'
    | 'default'
    | 'primary'
    | 'secondary';

export type ChipProps = Omit<MuiChipProps, 'color'> & {
    icon?: React.ReactNode;
    color?: ChipColorVariant;
};

interface ChipColor {
    background: string;
    dot: string;
    text: string;
}

// `primary`/`secondary` are accepted by ChipProps for MUI parity but have no
// palette entry here — excluding them keeps the six defined variants required,
// so direct lookups like chipColors.success.text stay non-nullable. Unknown
// variants fall back to `default` at the call site in Chip().
export const chipColors: Record<
    Exclude<ChipColorVariant, 'primary' | 'secondary'>,
    ChipColor
> = {
    success: {
        background: '#ECFDF3',
        dot: '#10B981',
        text: '#047857'
    },
    info: {
        background: '#EFF8FF',
        dot: '#175CD3',
        text: '#175CD3'
    },
    muted: {
        background: '#F2F4F7',
        dot: '#667085',
        text: '#344054'
    },
    error: {
        background: '#FEE4E2',
        dot: '#F04438',
        text: '#D92D20'
    },
    warning: {
        background: '#FFFBEB',
        dot: '#F59E0B',
        text: '#B45309'
    },
    default: {
        background: '#ECFDF3',
        dot: '#10B981',
        text: '#047857'
    }
};

export default function Chip({ color, icon, label, ...props }: ChipProps) {
    const colorObject =
        (color && color in chipColors
            ? chipColors[color as keyof typeof chipColors]
            : undefined) ?? chipColors.default;

    return (
        <MuiChip
            variant="filled"
            style={{
                backgroundColor: colorObject.background,
                color: colorObject.text,
                padding: '0px 0px',
                height: '22px',
                display: 'flex',
                width: 'fit-content'
            }}
            label={label}
            {...props}
            icon={
                icon ?? (
                    <CircleIcon
                        style={{
                            color: colorObject.dot,
                            height: '8px',
                            width: '8px'
                        }}
                        fontSize={'2px' as any}
                    />
                )
            }
        >
            {props.children}
        </MuiChip>
    );
}
